/**
 * Detect a newer deploy while a tab stays open. The build emits `version.json`
 * next to `index.html` (see `vite.config.ts`, `buildVersionFile`) carrying the
 * git SHA the deploy also passes as `VITE_SENTRY_RELEASE`; a running tab
 * compares that file against its own build id.
 */
export const VERSION_FILE_PATH = '/version.json';
export const VERSION_CHECK_INTERVAL_MS = 5 * 60_000;
// A tab regaining focus re-checks, but not more often than this.
export const VERSION_CHECK_MIN_GAP_MS = 60_000;

/**
 * Read the deployed build id. Resolves `null` for anything other than a
 * well-formed answer (network failure, 404 in a local build, malformed body)
 * — the caller treats `null` as "unknown", never as "changed".
 */
export async function fetchRemoteBuildId(
  fetchFn: typeof fetch = (...args) => fetch(...args),
): Promise<string | null> {
  try {
    const response = await fetchFn(VERSION_FILE_PATH, { cache: 'no-store' });
    if (!response.ok) return null;
    const body: unknown = await response.json();
    const buildId =
      typeof body === 'object' && body !== null
        ? (body as { buildId?: unknown }).buildId
        : undefined;
    return typeof buildId === 'string' && buildId.length > 0 ? buildId : null;
  } catch {
    return null;
  }
}

export type DeployedBuildComparison = {
  /** `unreachable` = the deployed id is unknown (offline, or no version.json served). */
  verdict: 'newer' | 'same' | 'unreachable';
  remoteBuildId: string | null;
};

/**
 * One-shot answer to "is the server serving a different build than this
 * tab runs?" — what a chunk-load failure needs in order to tell a stale
 * build (reload fixes it) from a transient fetch failure (same build, chunk
 * still there) or a dead network (version.json itself unreachable).
 */
export async function compareDeployedBuild({
  currentBuildId,
  fetchBuildId = fetchRemoteBuildId,
}: {
  currentBuildId: string;
  fetchBuildId?: () => Promise<string | null>;
}): Promise<DeployedBuildComparison> {
  const remoteBuildId = await fetchBuildId();
  if (remoteBuildId === null) return { verdict: 'unreachable', remoteBuildId };
  return { verdict: remoteBuildId === currentBuildId ? 'same' : 'newer', remoteBuildId };
}

export type VersionCheckOptions = {
  currentBuildId: string;
  /** Fired once, the first time a different build id is observed. */
  onNewVersion: (remoteBuildId: string) => void;
  fetchBuildId?: () => Promise<string | null>;
  intervalMs?: number;
  minGapMs?: number;
};

/**
 * Poll for a newer build on an interval and whenever the tab becomes visible
 * again (throttled by `minGapMs`). Returns a stop function.
 */
export function startVersionCheck({
  currentBuildId,
  onNewVersion,
  fetchBuildId = fetchRemoteBuildId,
  intervalMs = VERSION_CHECK_INTERVAL_MS,
  minGapMs = VERSION_CHECK_MIN_GAP_MS,
}: VersionCheckOptions): () => void {
  // The page load itself is the first check.
  let lastCheckedAt = Date.now();
  let stopped = false;
  let notified = false;

  const check = async (): Promise<void> => {
    if (stopped || notified) return;
    lastCheckedAt = Date.now();
    const remote = await fetchBuildId();
    if (stopped || notified || remote === null || remote === currentBuildId) return;
    notified = true;
    onNewVersion(remote);
  };

  const timer = setInterval(() => {
    void check();
  }, intervalMs);

  const onVisibilityChange = (): void => {
    if (document.visibilityState !== 'visible') return;
    if (Date.now() - lastCheckedAt < minGapMs) return;
    void check();
  };
  document.addEventListener('visibilitychange', onVisibilityChange);

  return () => {
    stopped = true;
    clearInterval(timer);
    document.removeEventListener('visibilitychange', onVisibilityChange);
  };
}
